import { Button } from "primereact/button";
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import PlanGroupActions from "../../Redux/transition/plan-group/plan-group.reducer";
import PlanActions from "../../Redux/transition/plan/plan.reducer";
import PaySwitchActions from "../../Redux/transition/payswitch/payswitch.reducer";
import { Card } from "primereact/card";
import { useFormik } from "formik";
import { Toast } from "primereact/toast";
import "../../Styles/pages/planCreate.scss";
import { Checkbox } from 'primereact/checkbox';
import { useLocation, useNavigate } from "react-router-dom";
import { removeTrailingSpacesAndDashes } from "../../Components/CleanInputs";
import { CustomValidationMessage } from "../../Components/CustomMessage";
import { useCurrencyConstants } from '../../Components/CurrencyFormatter';
import { getAutoBillingSupport } from "../../Service/api";
import {
  WIZARD_TYPES,
  applyWizardPresets,
  buildPolicyOption,
  getBillingPeriods,
  getWizardHeaderKey,
  getWizardSteps,
  getRemindersDisabledReason,
} from "../../constants/planWizardTypes";
import { validatePlanWizardFull, validatePlanWizardStep } from "./planWizardValidation";
import KindChooserStep from "./PlanCreateSteps/KindChooserStep";
import DetailsStep from "./PlanCreateSteps/DetailsStep";
import AmountStep from "./PlanCreateSteps/AmountStep";
import RemindersStep from "./PlanCreateSteps/RemindersStep";
import ReviewStep from "./PlanCreateSteps/ReviewStep";
import PlanWizardStepper from "./PlanCreateSteps/PlanWizardStepper";

const PlanCreate = (props) => {
  const defMembership = useSelector((state) => state.defMembership.defMembership);
  const business = useSelector((state) => state.businesses.business);
  const creating = useSelector((state) => state.plans.creating);
  const errorPlan = useSelector((state) => state.plans.errorCreating);
  const successPlan = useSelector((state) => state.plans.plan);
  const planGroupsGraphedActive = useSelector((state) => state.planGroups.planGroupsGraphedActive);

  const dispatch = useDispatch();
  const createPlanTier = useCallback((plan) => { dispatch(PlanActions.planCreateRequest(plan)); }, [dispatch]);
  const getActivePlanGroups = useCallback((businessId) => { dispatch(PlanGroupActions.planGroupGraphedActiveRequest(businessId)); }, [dispatch]);
  const getCredentials = useCallback((businessId, provider) => { dispatch(PaySwitchActions.paySwitchCredentialRequest(businessId, provider)); }, [dispatch]);

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("common");

  const initialWizardType = location.state?.wizardType || null;

  const [wizardType, setWizardType] = useState(initialWizardType);
  const [activeStep, setActiveStep] = useState(initialWizardType ? 0 : -1);
  const [flex, setFlex] = useState(false);
  const [created, setCreated] = useState(0);
  const [tiersCreated, setTiersCreated] = useState(1);
  const [saved, setSaved] = useState('');
  const [checked, setChecked] = useState(false);
  const [recoverMissed, setRevoverMissed] = useState(false);
  const [notRecover, setNotRevover] = useState(true);
  const [equalParts, setEqualParts] = useState(true);
  const [varying, setVarying] = useState(false);
  const [submit, setSubmit] = useState(0);
  const [lockButton, setLockButton] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [resetReminder, setResetReminder] = useState(0);
  const [supportsAutoBilling, setSupportsAutoBilling] = useState(false);

  const { currencyFormat, currencySymbol } = useCurrencyConstants();

  const [state, setState] = useState({
    creatingPlans: false,
    failure: false,
    targetEqualParts: true,
  });

  const groups = planGroupsGraphedActive?.groups?.map((item) => item.group);
  const planGroupExist = location.state?.plan;
  const planGroup = location.state?.planGroup;
  const toastPlan = useRef(null);
  const toastBL = useRef(null);
  const planGroups = groups;
  const tierExist = planGroup;

  const period = useMemo(
    () => (wizardType ? getBillingPeriods(wizardType, t) : []),
    [wizardType, t]
  );

  const fetchActive = () => {
    getActivePlanGroups(defMembership.business.id);
  };

  useEffect(() => {
    const savedVal = localStorage.getItem("created");
    setSaved(savedVal);
  }, [saved]);

  useEffect(() => {
    if (defMembership?.business?.id) {
      fetchActive();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defMembership?.business?.id]);

  useEffect(() => {
    let active = true;
    const businessId = defMembership?.business?.id;
    if (!businessId) {
      setSupportsAutoBilling(false);
      return () => { active = false; };
    }
    getAutoBillingSupport(businessId).then((response) => {
      if (active) {
        setSupportsAutoBilling(response.ok && response.data?.supportsAutoBilling === true);
      }
    });
    return () => { active = false; };
  }, [defMembership?.business?.id]);

  useEffect(() => {
    if (submit > 0) {
      formik.handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submit]);

  useEffect(() => {
    setState((prev) => ({ ...prev, creatingPlans: creating }));
    if (state.creatingPlans && state.failure && errorPlan) {
      showErrorPlan();
      setState((prev) => ({ ...prev, failure: false }));
    }
    if (state.creatingPlans && state.failure && successPlan) {
      showSuccessPlan();
      setState((prev) => ({ ...prev, failure: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creating, errorPlan, saved]);

  const showSuccessPlan = () => {
    toastPlan.current.show({
      severity: "success",
      summary: t("success"),
      detail: t("plan.save_success"),
      life: 3000,
    });
  };

  const showErrorPlan = () => {
    toastPlan.current.show({
      severity: "error",
      summary: t("products.generic_error"),
      sticky: true,
    });
  };

  const getBillingPeriodMax = () => {
    const billingPeriod = formik?.values.billingPeriod?.code || formik?.values.billingPeriod;
    switch (billingPeriod) {
      case 'DAILY': return 365;
      case 'DAILY_WITH_SATURDAYS': return 365;
      case 'DAILY_WITH_WEEKENDS': return 365;
      case 'WEEKLY': return 52;
      case 'BIWEEKLY': return 26;
      case 'MONTHLY': return 12;
      case 'QUARTERLY': return 12;
      case 'HALFYEARLY': return 6;
      case 'YEARLY': return 3;
      case 'ONCE': return 1;
      default: return 365;
    }
  };

  const amountCurrency = defMembership?.business?.currency;

  const getFixedAmountRangeError = (currency, min, max) => (
    <Trans
      i18nKey="plan.fixed_amount_range"
      values={{ currency, minAmount: min, maxAmount: max }}
      components={{
        1: <span className="trans_error_detail" />,
        2: <span className="trans_error_detail" />,
        3: <span className="trans_error_detail" />,
        4: <span className="trans_error_detail" />,
        5: <span className="trans_error_detail" />,
        6: <span className="trans_error_detail" />,
      }}
    />
  );

  const formik = useFormik({
    initialValues: {
      id: null,
      name: "",
      description: "",
      billingPeriod: null,
      deductions: undefined,
      groupName: null,
      graceDays: undefined,
      reminderDays: undefined,
      policy: null,
      cost: null,
      planId: null,
      setup: undefined,
      feeCheck: false,
      flex: false,
      targetEqualParts: true,
      recoverMissed: false,
      autoBilling: false,
    },
    validate: () => ({}),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (data) => {
      const groupData = () => {
        if (planGroupExist) {
          return { business: planGroup.business, id: planGroup.id };
        }
        if (planGroups?.length <= 1) {
          return { business: planGroups?.[0]?.business, id: planGroups?.[0]?.id };
        }
        return { business: data.planId?.business, id: data.planId?.id };
      };

      const planTierData = {
        name: removeTrailingSpacesAndDashes(data.name) || null,
        description: removeTrailingSpacesAndDashes(data.description) || null,
        id: data.id || null,
        cost: data.cost || null,
        billingPeriod: data.billingPeriod?.code || null,
        business: groupData().business,
        deductions: data.deductions || null,
        groupId: groupData().id,
        graceDays: data.graceDays || null,
        reminderDays: data.reminderDays || null,
        policy: data.policy?.code || null,
        flex,
        setup: (data.billingPeriod?.code === "ONCE" || data.autoBilling ? null : data.setup) || null,
        targetEqualParts: data.policy?.code === "TARGET" ? data.targetEqualParts : false,
        recoverMissed: data.policy?.code === "TARGET" ? data.recoverMissed : false,
        collectionMode: supportsAutoBilling
          && ["FIXED", "CUSTOM"].includes(data.policy?.code)
          && data.autoBilling
          ? "AUTOBILL"
          : "REMINDER",
      };
      setTiersCreated((prev) => prev + 1);
      setState((prev) => ({ ...prev, failure: true }));
      createPlanTier(planTierData);
      localStorage.setItem("planNum", JSON.stringify(planGroup?.id));
      localStorage.setItem("created", JSON.stringify(created));
      localStorage.setItem("tiersCreated", JSON.stringify(tiersCreated));
      fetchActive();
    },
  });

  const wizardSteps = useMemo(
    () => (wizardType ? getWizardSteps(wizardType, formik.values.billingPeriod) : []),
    [wizardType, formik.values.billingPeriod]
  );

  const reviewStepIndex = wizardSteps.length > 0 ? wizardSteps.length - 1 : 0;
  const activeWizardStepId = wizardSteps[activeStep]?.id;

  useEffect(() => {
    if (!wizardType || wizardSteps.length === 0) {
      return;
    }
    const maxIndex = wizardSteps.length - 1;
    setActiveStep((prev) => (prev > maxIndex ? maxIndex : prev));
  }, [wizardType, wizardSteps.length]);

  useEffect(() => {
    if (initialWizardType) {
      const periods = getBillingPeriods(initialWizardType, t);
      applyWizardPresets(initialWizardType, formik, t, periods);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const nextWizardType = location.state?.wizardType ?? null;
    if (nextWizardType === wizardType) {
      return;
    }
    setWizardType(nextWizardType);
    setActiveStep(nextWizardType ? 0 : -1);
    if (nextWizardType) {
      const periods = getBillingPeriods(nextWizardType, t);
      applyWizardPresets(nextWizardType, formik, t, periods);
      if (nextWizardType !== WIZARD_TYPES.TARGET) {
        setEqualParts(true);
        setVarying(false);
        setRevoverMissed(false);
        setNotRevover(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.state?.wizardType]);

  useEffect(() => {
    if (formik.values?.policy?.code === "FLEXIBLE") {
      setFlex(true);
    } else {
      setFlex(false);
    }
    if (!["FIXED", "CUSTOM"].includes(formik.values?.policy?.code) && formik.values.autoBilling) {
      formik.setFieldValue("autoBilling", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values?.policy]);

  const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
  const getFormErrorMessage = (name) => (
    isFormFieldValid(name) && (
      <CustomValidationMessage style={{ marginLeft: "0.3rem" }} severity="error" text={formik.errors[name]} />
    )
  );
  const isFormFieldValidCost = (cost) => !!(formik.touched[cost] && formik.errors[cost]);
  const getFormErrorMessageCost = (cost) => (
    isFormFieldValidCost(cost) && <CustomValidationMessage severity="error" text={formik.errors[cost]} />
  );
  const isFormFieldValidSetupFee = (setup) => !!(formik.touched[setup] && formik.errors[setup]);
  const getFormErrorMessageSetupFee = (setup) => (
    isFormFieldValidSetupFee(setup) && <CustomValidationMessage severity="error" text={formik.errors[setup]} />
  );
  const isFormFieldValidBillling = (billingPeriod) => !!(formik.touched[billingPeriod] && formik.errors[billingPeriod]);
  const getFormErrorMessageBilling = (billingPeriod) => (
    isFormFieldValidBillling(billingPeriod) && <CustomValidationMessage severity="error" text={formik.errors[billingPeriod]} />
  );
  const isFormFieldValidPlanId = (planId) => !!(formik.touched[planId] && formik.errors[planId]);
  const getFormErrorMessagePlanId = (planId) => (
    isFormFieldValidPlanId(planId) && <CustomValidationMessage severity="error" text={formik.errors[planId]} />
  );
  const isFormFieldValidDeduction = (deductions) => !!(formik.touched[deductions] && formik.errors[deductions]);
  const getFormErrorMessageDeduction = (deductions) => (
    isFormFieldValidDeduction(deductions) && <CustomValidationMessage severity="error" text={formik.errors[deductions]} />
  );
  const isFormFieldValidReminderDays = (reminderDays) => !!(formik.touched[reminderDays] && formik.errors[reminderDays]);
  const getFormErrorMessageReminderDays = (reminderDays) => (
    isFormFieldValidReminderDays(reminderDays) && (
      <div style={{ marginTop: '5px' }}>
        <p className="reminderDays_hint_error">{formik.errors[reminderDays]}</p>
      </div>
    )
  );
  const isFormFieldValidGraceDays = (graceDays) => !!(formik.touched[graceDays] && formik.errors[graceDays]);
  const getFormErrorMessageGraceDays = (graceDays) => (
    isFormFieldValidGraceDays(graceDays) && (
      <div style={{ marginTop: '5px' }}>
        <p className="graceDays_hint_error">{formik.errors[graceDays]}</p>
      </div>
    )
  );

  const validationParams = {
    wizardType,
    data: formik.values,
    t,
    planGroupExist,
    planGroups,
    business,
    amountCurrency,
    getFixedAmountRangeError,
    getBillingPeriodMax,
  };

  const applyWizardValidationErrors = (errors) => {
    const touched = { ...formik.touched };
    Object.keys(errors).forEach((key) => {
      touched[key] = true;
    });
    formik.setTouched(touched, false);
    formik.setErrors(errors);
  };

  const runStepValidation = (stepIndex) => {
    const stepId = wizardSteps[stepIndex]?.id;
    const errors = validatePlanWizardStep({ ...validationParams, stepIndex, stepId });
    applyWizardValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSelectKind = (type) => {
    const periods = getBillingPeriods(type, t);
    applyWizardPresets(type, formik, t, periods);
    setWizardType(type);
    setActiveStep(0);
    if (type !== WIZARD_TYPES.TARGET) {
      setEqualParts(true);
      setVarying(false);
      setRevoverMissed(false);
      setNotRevover(true);
    }
    navigate(location.pathname, {
      replace: true,
      state: { ...location.state, wizardType: type, plan: planGroupExist, planGroup },
    });
  };

  const handleSelectFixed = () => {
    formik.setFieldValue('policy', buildPolicyOption('FIXED', t));
    if (formik.errors.policy) {
      formik.setFieldError('policy', undefined);
    }
    setFlex(false);
  };

  const handleSelectFlexible = () => {
    formik.setFieldValue('policy', buildPolicyOption('FLEXIBLE', t));
    formik.setFieldValue('cost', null);
    formik.setFieldValue('autoBilling', false);
    if (formik.errors.policy) {
      formik.setFieldError('policy', undefined);
    }
    if (formik.errors.cost) {
      formik.setFieldError('cost', undefined);
    }
    setFlex(true);
  };

  const onChangeBillingPeriod = (e) => {
    formik.setFieldValue("billingPeriod", e.value);
    if (formik.errors.billingPeriod) {
      formik.setFieldError("billingPeriod", undefined);
    }
    if (getRemindersDisabledReason(e.value) !== null) {
      formik.setFieldValue("graceDays", undefined);
      formik.setFieldValue("reminderDays", undefined);
      formik.setFieldError("graceDays", undefined);
      formik.setFieldError("reminderDays", undefined);
    }
    if (e.value?.code === "ONCE") {
      formik.setFieldError("deductions", "");
      formik.setFieldValue("deductions", 1);
      formik.setFieldValue("feeCheck", false);
      formik.setFieldValue("setup", undefined);
    } else if (wizardType !== WIZARD_TYPES.SINGLE) {
      formik.setFieldValue("deductions", '');
    }
  };

  const onChangePlanGroup = (e) => {
    formik.setFieldValue("planId", e.value);
    if (formik.errors.planId) {
      formik.setFieldError("planId", undefined);
    }
  };

  const onChangeFeeCheck = (e) => {
    formik.setFieldValue("feeCheck", e.checked);
    if (formik.errors.setup) {
      formik.setFieldError("setup", undefined);
    }
    if (!e.checked) {
      formik.setFieldValue("setup", undefined);
    }
  };

  const onChangeRecover = () => {
    setRevoverMissed(true);
    setNotRevover(false);
    formik.setFieldValue("recoverMissed", true);
  };

  const onChangeNotRecover = () => {
    setNotRevover(true);
    setRevoverMissed(false);
    formik.setFieldValue("recoverMissed", false);
  };

  const onChangeEqualParts = () => {
    setEqualParts(true);
    setVarying(false);
    setRevoverMissed(false);
    setNotRevover(true);
    setState((prev) => ({ ...prev, targetEqualParts: true }));
    formik.setFieldValue("targetEqualParts", true);
    formik.setFieldValue("recoverMissed", false);
  };

  const onChangeVarying = () => {
    setEqualParts(false);
    setVarying(true);
    setState((prev) => ({ ...prev, targetEqualParts: false }));
    formik.setFieldValue("targetEqualParts", false);
    formik.setFieldValue("recoverMissed", false);
  };

  const handleContinue = () => {
    if (!runStepValidation(activeStep)) {
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleWizardCancel = () => {
    const returnTo = location.state?.returnTo;
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/products');
  };

  const handleBack = () => {
    if (activeStep === 0) {
      handleWizardCancel();
      return;
    }
    setActiveStep((prev) => prev - 1);
  };

  const handleSave = () => {
    const errors = validatePlanWizardFull(validationParams);
    if (Object.keys(errors).length > 0) {
      applyWizardValidationErrors(errors);
      return;
    }
    setLockButton(true);
    localStorage.removeItem('toastShown');
    setSubmit((prev) => prev + 1);
  };

  const resetWizardAfterCreate = () => {
    formik.resetForm();
    setResetKey((prev) => prev + 1);
    setResetReminder((prev) => prev + 1);
    setEqualParts(true);
    setVarying(false);
    setRevoverMissed(false);
    setNotRevover(true);
    setActiveStep(0);
    if (wizardType) {
      const periods = getBillingPeriods(wizardType, t);
      applyWizardPresets(wizardType, formik, t, periods);
    }
  };

  const headerTitle = wizardType ? t(getWizardHeaderKey(wizardType)) : t("plan.create_plan");

  const header = (
    <div className="planHeader">
      <h3 className="headerText">{headerTitle}</h3>
    </div>
  );

  const sharedStepProps = {
    formik,
    t,
    Trans,
    business,
    tierExist,
    planGroupExist,
    planGroups,
    isFormFieldValid,
    getFormErrorMessage,
    isFormFieldValidPlanId,
    getFormErrorMessagePlanId,
    onChangePlanGroup,
  };

  const renderWizardStep = () => {
    if (activeWizardStepId === 'details') {
      return <DetailsStep {...sharedStepProps} />;
    }
    if (activeWizardStepId === 'amount') {
      return (
        <AmountStep
          wizardType={wizardType}
          period={period}
          supportsAutoBilling={supportsAutoBilling}
          onSelectFixed={handleSelectFixed}
          onSelectFlexible={handleSelectFlexible}
          onChangeBillingPeriod={onChangeBillingPeriod}
          onChangeFeeCheck={onChangeFeeCheck}
          equalParts={equalParts}
          varying={varying}
          onChangeEqualParts={onChangeEqualParts}
          onChangeVarying={onChangeVarying}
          recoverMissed={recoverMissed}
          notRecover={notRecover}
          onChangeRecover={onChangeRecover}
          onChangeNotRecover={onChangeNotRecover}
          getBillingPeriodMax={getBillingPeriodMax}
          isFormFieldValidCost={isFormFieldValidCost}
          getFormErrorMessageCost={getFormErrorMessageCost}
          isFormFieldValid={isFormFieldValid}
          getFormErrorMessageBilling={getFormErrorMessageBilling}
          isFormFieldValidDeduction={isFormFieldValidDeduction}
          getFormErrorMessageDeduction={getFormErrorMessageDeduction}
          isFormFieldValidSetupFee={isFormFieldValidSetupFee}
          getFormErrorMessageSetupFee={getFormErrorMessageSetupFee}
          amountCurrency={amountCurrency}
          {...sharedStepProps}
        />
      );
    }
    if (activeWizardStepId === 'reminders') {
      return (
        <RemindersStep
          resetKey={resetKey}
          resetReminder={resetReminder}
          getFormErrorMessageGraceDays={getFormErrorMessageGraceDays}
          getFormErrorMessageReminderDays={getFormErrorMessageReminderDays}
          {...sharedStepProps}
        />
      );
    }
    if (activeWizardStepId === 'review') {
      return (
        <ReviewStep
          wizardType={wizardType}
          currencySymbol={currencySymbol}
          currencyFormat={currencyFormat}
          equalParts={equalParts}
          recoverMissed={recoverMissed}
          {...sharedStepProps}
        />
      );
    }
    return null;
  };

  const isReviewStep = activeStep === reviewStepIndex;

  return (
    <Card header={header} style={{ width: "100%", marginLeft: "0rem" }}>
      <Toast
        ref={toastBL}
        className="toastBL"
        position="bottom-left"
        onRemove={(message) => {
          if (message.severity === 'warn') {
            setCreated('');
          }
        }}
      />
      <Toast
        className="p-toast p-toast-container p-toast-item p-toast-message p-toast-title"
        ref={toastPlan}
        onRemove={(message) => {
          if (message.severity === 'success' && checked) {
            resetWizardAfterCreate();
            setCreated((prev) => prev + 1);
            setSubmit(0);
          } else if (message.severity === 'success' && !checked) {
            navigate("/products");
            formik.resetForm();
            setCreated(0);
            setSubmit(0);
            getCredentials(business.id, business.pmtProvider);
          }
          setLockButton(false);
        }}
      />

      {!wizardType && (
        <KindChooserStep t={t} onSelectKind={handleSelectKind} />
      )}

      {wizardType && activeStep >= 0 && (
        <>
          <PlanWizardStepper steps={wizardSteps} activeStep={activeStep} t={t} />
          <div className="plan-wizard-step-content">
            {renderWizardStep()}
          </div>
        </>
      )}

      {wizardType && activeStep >= 0 && (
        <>
          <p className="second_hr" />
          <div className="footer plan-wizard-footer">
            <div style={{ display: "flex", flexDirection: "row", marginLeft: "0.5rem" }}>
              <Checkbox onChange={(e) => setChecked(e.checked)} checked={checked} />
              <p className="checkbox_label">{t("plan.checkbox_label")}</p>
            </div>
            <div style={{ marginTop: "0.2rem" }}>
              <i className="pi pi-check check-icon" />
              <span className="tiers_created">
                {created} {created <= 1 ? t("plan.plan_created") : t("plan.plans_created")}
              </span>
            </div>
            <div className="plan-wizard-nav-buttons">
              <Button
                type="button"
                label={activeStep === 0 ? t('plan.wizard.cancel') : t('plan.wizard.back')}
                className="p-button-outlined"
                onClick={handleBack}
                disabled={creating}
              />
              {!isReviewStep ? (
                <Button
                  type="button"
                  id="plan_wizard_continue"
                  label={t("plan.wizard.continue")}
                  onClick={handleContinue}
                  disabled={creating}
                />
              ) : (
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={creating || lockButton}
                  loading={creating}
                  label={t("plan.save_plan")}
                  id="tier_save"
                />
              )}
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default PlanCreate;
